import React from 'react';
import {
  FormattedDate,
  FormattedRelativeTime,
  FormattedTime
} from 'react-intl';
import { RelativeTimeFormatSingularUnit } from '@formatjs/ecma402-abstract';
import { get, isEmpty, isFinite, pick } from 'lodash';
import moment from 'moment-timezone';
// @ts-ignore
import { IDateTimeFormats } from '@broadleaf/admin-components/dist/types';

/**
 * Renders a time as 10/10/2024 10:00 AM
 */
const shortTimeFormat = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
};

export const ShortDateTime: React.FC<ShortDateTimeProps> = ({
  className,
  value,
  relative = false,
  dateTimeSeparator = ' ',
  format: formatString = 'LLL'
}) => {
  if (typeof value === 'string') {
    value = new Date(value);
  }

  if (!value || !isFinite(value.getTime())) {
    console.warn('Datetime provided was not valid!');
    return <span />;
  }

  const format = getDateTimeFormat(formatString);
  const dateFormat = pick(format, ['year', 'month', 'day']);

  return (
    // @ts-ignore
    <FormattedDate value={value} timeZone={moment().tz()} {...dateFormat}>
      {formattedDate => {
        const timeFormat = pick(format, [
          'hour',
          'minute',
          'second',
          'timeZoneName'
        ]);

        if (isEmpty(timeFormat)) {
          if (relative) {
            return getFormattedRelative({
              className,
              formattedDate,
              value
            });
          }

          return <span className={className}>{formattedDate}</span>;
        }

        return getFormattedTime({
          className,
          // @ts-ignore
          format,
          formattedDate,
          relative,
          dateTimeSeparator,
          value
        });
      }}
    </FormattedDate>
  );
};

export interface ShortDateTimeProps {
  /**
   * Class names to add to the formatted date time
   */
  className?: string;
  /** Character to use to separate the date and the time. Default is just a space. */
  dateTimeSeparator?: string;
  /**
   * Optional prop to allow specifying how to format the dateTime. Default is
   * {@link FormatType.LLL}, e.g., January 1, 2019 1:00 AM. To add time zone,
   * use `+Z` for long (British Summer Time) or `+z` for short (GMT+1).
   */
  format?: IDateTimeFormats;
  /**
   * Datetime string or date to format. If a string, it should conform to
   * ISO 8601.
   */
  value: string | Date;
  /**
   * Whether to format the given <code>value</value> as relative to 'now':
   * 10 seconds ago, 1 day ago, etc.
   */
  relative?: boolean;
}

const getDateTimeFormat = (formatString: string) => {
  if (!formatString) return FormatType.LLL;
  if (formatString === 'SHORT') return shortTimeFormat;

  const parts = formatString.split('+');
  const format = get(FormatType, parts[0], {});

  if (parts.length === 1) return format;

  return { ...format, ...get(TimeZoneFormatType, parts[1], {}) };
};

const getFormattedTime: React.FC<GetFormatedTimeProps> = ({
  className,
  format,
  formattedDate,
  relative,
  dateTimeSeparator,
  value
}) => {
  const timeFormat = pick(format, [
    'hour',
    'minute',
    'second',
    'timeZoneName'
  ]) as Object;

  return (
    // @ts-ignore
    <FormattedTime value={value} timeZone={moment().tz()} {...timeFormat}>
      {formattedTime => {
        if (relative) {
          return getFormattedRelative({
            className,
            formattedDate,
            formattedTime,
            value
          });
        }

        return (
          <span
            className={className}
          >{`${formattedDate}${dateTimeSeparator}${formattedTime}`}</span>
        );
      }}
    </FormattedTime>
  );
};

interface GetFormatedTimeProps extends ShortDateTimeProps {
  formattedDate: string;
}

const getFormattedRelative: React.FC<GetFormattedRelativeProps> = ({
  className,
  formattedDate,
  formattedTime = '',
  value: timestamp
}) => {
  const { unit, value } = calculateRelativeTime(timestamp);
  return (
    <FormattedRelativeTime
      numeric="auto"
      style="long"
      unit={unit}
      value={value}
    >
      {formattedRelative => (
        <span className={className} title={`${formattedDate} ${formattedTime}`}>
          {formattedRelative}
        </span>
      )}
    </FormattedRelativeTime>
  );
};

interface GetFormattedRelativeProps extends ShortDateTimeProps {
  formattedDate?: string;
  formattedTime?: string;
}
/**
 * Calculates the relative value and unit from now to the provided timestamp.
 *
 * @param {String} timestamp the ISO8601 timestamp
 * @returns {{unit: string, value: number}}
 */
const calculateRelativeTime = (
  timestamp: string | Date
): {
  unit: RelativeTimeFormatSingularUnit;
  value: number;
} => {
  const currentMoment = moment();
  const timestampMoment = moment(timestamp);
  const elapsed = currentMoment.diff(timestampMoment);
  const msPerSecond = 1000;
  const msPerMinute = 60 * msPerSecond;
  const msPerHour = 60 * msPerMinute;
  const msPerDay = 24 * msPerHour;
  const msPerWeek = 7 * msPerDay;
  const msPerMonth = 30 * msPerDay;
  const msPerYear = 365 * msPerDay;
  if (elapsed < msPerMinute) {
    return {
      unit: 'second',
      value: -Math.floor(elapsed / msPerSecond)
    };
  } else if (elapsed < msPerHour) {
    return {
      unit: 'minute',
      value: -Math.floor(elapsed / msPerMinute)
    };
  } else if (elapsed < msPerDay) {
    return {
      unit: 'hour',
      value: -Math.floor(elapsed / msPerHour)
    };
  } else if (elapsed < msPerWeek) {
    return {
      unit: 'day',
      value: -Math.floor(elapsed / msPerDay)
    };
  } else if (elapsed < msPerMonth) {
    return {
      unit: 'week',
      value: -Math.floor(elapsed / msPerWeek)
    };
  } else if (elapsed < msPerYear) {
    return {
      unit: 'month',
      value: -Math.floor(elapsed / msPerMonth)
    };
  } else {
    return {
      unit: 'year',
      value: -Math.floor(elapsed / msPerYear)
    };
  }
};

const TimeZoneFormatType = Object.freeze({
  /** Central Daylight Time */
  Z: {
    timeZoneName: 'long'
  },
  /** CDT */
  z: {
    timeZoneName: 'short'
  }
});

const FormatType = Object.freeze({
  /** 06/09/2014 */
  L: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  },
  /** 6/9/2014 */
  l: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  },
  /** January 9 2014 */
  LL: {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  },
  /** Jan 9 2014 */
  ll: {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  },
  /** January 9 2014 9:32 PM */
  LLL: {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  },
  /** Jan 9 2014 9:32 PM */
  lll: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  },
  /** Monday, January 9 2014 9:32 PM */
  LLLL: {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
    hour: 'numeric',
    minute: '2-digit'
  },
  /** Mon, Jan 9 2014 9:32 PM */
  llll: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit'
  }
});
